import apiClient from './api.service'
import { setToken, removeToken, getToken, isAuthenticated } from './token.service'

export interface User {
  id: number
  username: string
  nickname: string
  email: string
  mobile: string | null
  avatar: string | null
  is_admin?: boolean
}

export interface LoginCredentials {
  username: string
  password: string
}

export interface RegisterData {
  username: string
  password: string
  email?: string
  mobile?: string
}

export interface AuthResponse {
  success: boolean
  message: string
  data: {
    token: string
    user: User
  }
}

interface LoginResponse {
  success: boolean
  message: string
  data?: {
    token: string
    user: {
      id: number
      username: string
      nickname: string
      email: string
      mobile: string | null
      avatar: string | null
    }
  }
}

const authService = {
  // 登录
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      console.log('Sending login request to:', '/auth/login')
      console.log('With credentials:', credentials)

      // 使用apiClient发送请求，使用相对路径（baseURL已包含/api）
      const response = await apiClient.post<LoginResponse>('/auth/login', credentials)
      console.log('Login response:', response.data)

      if (response.data.success && response.data.data?.token) {
        setToken(response.data.data.token)
        // 保存用户信息
        if (response.data.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.data.user))
        }
        return response.data
      } else {
        console.error('Login failed:', response.data.message)
        return response.data
      }
    } catch (error: any) {
      console.error('Login error:', error)
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers,
      })

      // 创建一个默认的错误响应
      const errorResponse: LoginResponse = {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed',
      }

      throw errorResponse
    }
  },

  // 登出
  logout() {
    removeToken()
    window.location.href = '/auth/login'
  },

  // 获取用户信息
  async getProfile() {
    return await apiClient.get('/auth/profile')
  },

  // 注册
  async register(data: RegisterData) {
    const response = await apiClient.post<AuthResponse>('/auth/register', data)
    if (response.data.success) {
      setToken(response.data.data.token)
      localStorage.setItem('user', JSON.stringify(response.data.data.user))
    }
    return response.data
  },

  // 更新用户信息
  async updateProfile(data: Partial<User>) {
    const response = await apiClient.put<{ success: boolean; data: User }>('/auth/profile', data)
    if (response.data.success) {
      localStorage.setItem('user', JSON.stringify(response.data.data))
    }
    return response.data
  },

  // 修改密码
  async changePassword(oldPassword: string, newPassword: string) {
    return apiClient.put<{ success: boolean; message: string }>('/auth/password', {
      oldPassword,
      newPassword,
    })
  },

  // 忘记密码
  async forgotPassword(data: { email: string }) {
    return apiClient.post<{ success: boolean; message: string }>('/auth/forgot-password', data)
  },

  // 验证重置密码令牌
  async verifyResetToken(data: { token: string }) {
    return apiClient.post<{ success: boolean; message: string }>('/auth/verify-reset-token', data)
  },

  // 重置密码
  async resetPassword(data: { token: string; password: string }) {
    return apiClient.post<{ success: boolean; message: string }>('/auth/reset-password', data)
  },

  // 检查是否已登录
  isAuthenticated(): boolean {
    return isAuthenticated()
  },

  // 获取Token
  getToken(): string | null {
    return getToken()
  },

  // 获取用户信息
  getUser(): User | null {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  },

  // 检查是否为管理员
  isAdmin(): boolean {
    const user = this.getUser()
    return !!user && !!user.is_admin
  },
}

export default authService
